/**
 * AuthProvider — Magic link auth (localStorage JWT).
 * VITE_AUTH_DISABLED=true → mock user (dev mode).
 * Otherwise → reads JWT from localStorage (set by Login page).
 */
import React, { createContext, useContext, useEffect, useMemo } from "react";

export interface AuthUser {
  sub: string;
  name: string;
  email: string;
  role: string;
}

interface AuthContextValue {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: AuthUser | null;
  token: string | null;
  role: string;
  isAdmin: boolean;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue>({
  isAuthenticated: false,
  isLoading: false,
  user: null,
  token: null,
  role: "user",
  isAdmin: false,
  logout: () => {},
});

const AUTH_DISABLED = import.meta.env.VITE_AUTH_DISABLED === "true";

const MOCK_USER: AuthUser = {
  sub: "dev-user",
  name: "Operatore Test",
  email: "operatore@trustycert.local",
  role: "admin",
};

function _readAuth(): { user: AuthUser | null; token: string | null; role: string } {
  try {
    const token = localStorage.getItem("trustycert_token");
    const raw = localStorage.getItem("trustycert_user");
    if (!token || !raw) return { user: null, token: null, role: "user" };
    const u = JSON.parse(raw);
    // Decode JWT payload (base64url, no signature verification — client-side only)
    const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    if (payload.exp && payload.exp * 1000 < Date.now()) {
      localStorage.removeItem("trustycert_token");
      localStorage.removeItem("trustycert_user");
      return { user: null, token: null, role: "user" };
    }
    const role: string = payload.role || "user";
    return {
      user: { sub: u.sub || "", name: u.name || "", email: u.email || "", role },
      token,
      role,
    };
  } catch {
    return { user: null, token: null, role: "user" };
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const value = useMemo<AuthContextValue>(() => {
    if (AUTH_DISABLED) {
      return {
        isAuthenticated: true, isLoading: false, user: MOCK_USER, token: null,
        role: "admin", isAdmin: true,
        logout: () => { window.location.href = "/"; },
      };
    }
    const { user, token, role } = _readAuth();
    return {
      isAuthenticated: !!user,
      isLoading: false,
      user,
      token,
      role,
      isAdmin: role === "admin",
      logout: () => {
        localStorage.removeItem("trustycert_token");
        localStorage.removeItem("trustycert_user");
        window.location.href = "/login";
      },
    };
  }, []);

  // Auto-redirect to login on 401 (token expired while using the app)
  useEffect(() => {
    const handler = () => {
      localStorage.removeItem("trustycert_token");
      localStorage.removeItem("trustycert_user");
      window.location.href = "/login";
    };
    window.addEventListener("auth:unauthorized", handler);
    return () => window.removeEventListener("auth:unauthorized", handler);
  }, []);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
